import Image, { ImageProps } from "next/image";

import { scale } from "@cloudinary/url-gen/actions/resize";

import {
  cloudinary,
  addCloudinaryParameters,
  getCloudinaryUrlWithoutParameters,
} from "~/util/cloudinary-utils";

type Props = Omit<ImageProps, "src"> & {
  src: string;
};

const cloudinaryRoute = "https://res.cloudinary.com/monthly/image/upload/";

export const ImageWithBlur = (imageProps: Props) => {
  const {
    src,
    layout,
    objectFit,
    objectPosition,
    style,
    width,
    height,
    ...restProps
  } = imageProps as any;

  if (typeof src !== "string" || !((src || "").indexOf(cloudinaryRoute) !== -1)) {
    // Handle deprecated props for non-Cloudinary images
    const {
      layout: _,
      objectFit: __,
      objectPosition: ___,
      ...cleanProps
    } = imageProps as any;

    // Convert deprecated props to modern style approach
    const modernStyle = {
      ...style,
      ...(objectFit && { objectFit }),
      ...(objectPosition && { objectPosition }),
    };

    // Handle different layout types
    if (layout === "fill" || layout === "responsive") {
      // Both fill and responsive now use fill prop with aspect ratio containers
      return <Image {...cleanProps} fill style={modernStyle} alt={imageProps.alt} />;
    }

    // For fixed layouts, include width and height
    return <Image {...cleanProps} width={width} height={height} style={modernStyle} alt={imageProps.alt} />;
  }

  const optimizedSrc = addCloudinaryParameters(src, ["q_auto:best"]);

  const blurredImage = cloudinary
    .image(src.replace(cloudinaryRoute, ""))
    .quality(1)
    .resize(scale(0.2, 0.2))
    .toURL();

  // Convert deprecated props to modern style approach
  const modernStyle = {
    ...style,
    ...(objectFit && { objectFit }),
    ...(objectPosition && { objectPosition }),
  };

  // Handle different layout types
  if (layout === "fill" || layout === "responsive") {
    // Both fill and responsive now use fill prop with aspect ratio containers
    return (
      <Image
        {...restProps}
        src={optimizedSrc}
        fill
        style={modernStyle}
        alt={imageProps.alt}
        unoptimized={true}
      />
    );
  }

  // For fixed layouts, include width and height
  return (
    <Image
      {...restProps}
      src={optimizedSrc}
      width={width}
      height={height}
      style={modernStyle}
      alt={imageProps.alt}
      unoptimized={true}
    />
  );
};
